/* eslint-disable react/no-danger */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HelpCenter.scss';

import { Toast } from 'primereact/toast';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';

import SearchIcon from '../../assets/icons/magnify.svg';
import CloseIcon from '../../assets/icons/input-close.svg';
import useAxios from '../../hooks/useAxios';
import { getPreviewUrl, handleError } from '../../utils';
import SpinnerOverlay from '../../components/SpinnerOverlay/SpinnerOverlay';
import { useAuth } from '../../hooks/useAuth';

const HelpCenter = () => {
  const toastBottom = useRef<any>(null);
  const navigate = useNavigate();
  
  const { user } = useAuth();
  const isLoggedIn = !!user;

  const { postArrayBuffer, post, apiLoading, apiError } = useAxios();
  
  const [searchText, setSearchText] = useState('');

  const onSearchSubmit = () => {

  };

  const faqs = [
    {
      question: 'How can I check the current status of an asset in a workflow?',
      answer: `
      <ul>
        <li>Navigate to the <strong>Dashboard</strong> from the top-left panel. At the top of the dashboard, you'll find summary cards displaying:
          <ul>
            <li>Workflows pending action</li>
            <li>Agency submissions</li>
            <li>Workflows currently under review</li>
            <li>Overdue workflows</li>
          </ul>
        </li>
        <li>For detailed information, scroll down to the <strong>Workflow Grid</strong>.  
        Use the toggle above the grid to switch between <strong>Active</strong> and <strong>Completed</strong> workflows.</li>
      </ul>
    `
    },
    {
      question: 'How can I search for approved claims?',
      answer: `
      <ul>
        <li>Go to the <strong>AI Portal </strong>on the left navigation panel and select the <strong>Collections </strong> tab.
        </li>
        <li>The Collections page lists all existing collections with details such as:
        <ul>
        <li>Collection name</li>
        <li>Category</li>
        <li>Brand and Sub-brand</li>
        <li>Default status</li>
        <li>Created date</li>
        <li>Available actions</li>
        </ul>
      </ul>
    `
    },
    {
      question: 'What are Clara’s suggestions based on?',
      answer: `
      <ul>
      <li>Clara’s suggestions are derived from <strong>approved claims</strong> created during Claims Workshops.</li>
      <li>The system continuously learns and updates suggestions to ensure relevance and accuracy.</li></ul>
      `
    },
    {
      question: 'How can I report a bug or an issue?',
      answer: `
      <ul><li>To report a bug or issue, please complete the form available  <a
              href="https://forms.office.com/Pages/ResponsePage.aspx?id=e7Oy_KBda0abgwAUtnp8eGNg5whi1ZhAhOht4eRDhKdURjFZOUxJSFBHTkxNUTdKVFNEUVhCUlJTUy4u"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#365EED', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500 }}
            >
              Bayer Consumer Health&apos;s LMR Platform Feedback
            </a></li>
      </ul>
      `
    }
  ];

  const getUserManual = async () => {
    if (isLoggedIn) {
      await postArrayBuffer('user-manager/user_manual_download', {})
        .then(async (response: any) => {
          const { status, data } = response;

          if ([200, 201].includes(status) && data) {
            if (data.type === 'application/json' || data.size < 1000) {
              const text = await data.text();
              try {
                const jsonResponse = JSON.parse(text);
                if (jsonResponse.errors?.length > 0) {
                  toastBottom.current.show({ severity: 'error', summary: 'Error', detail: 'Please log in to access the user manual.', life: 3000 });
                  setTimeout(() => {
                    navigate('/login');
                  }, 1500);
                  return;
                }
              } catch {
                // Not JSON, treat as PDF
              }
            }

            const blob = new Blob([data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
          }
        })
        .catch((error: any) => {
          if ([401, 404, 500].includes(error?.request?.status)) {
            handleError(error, toastBottom);
          }
        });
    } else {
      toastBottom.current.show({ severity: 'error', summary: 'Error', detail: 'Please log in to access the user manual.', life: 3000 });
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    }
  };

  const getReleaseNotes = async () => {
    if (isLoggedIn) {
      await post('user-manager/download_release_notes', { as_url: true })
        .then(async (response: any) => {
          const { status, data } = response;

          if ([200, 201].includes(status)) {
            if (data?.errors?.length) {
              toastBottom.current.show({ severity: 'error', summary: 'Error', detail: data.errors.join(', ') || 'No release notes file is available.', life: 3000 });
              return;
            }
            const fileInfo = data?.messages?.[0];
            if (fileInfo?.download_url) {
              const previewUrl = getPreviewUrl(fileInfo.download_url, fileInfo.file_name, fileInfo.file_size);
              if (previewUrl !== fileInfo.download_url) {
                // Office Online Viewer needs to fetch the file itself, so it must get the real SAS URL.
                window.open(previewUrl, '_blank');
              } else {
                // The SAS URL forces a download via Content-Disposition: attachment — fetch the bytes
                // ourselves into a blob so PDFs (and anything the browser can render) open inline instead.
                const fileResponse = await fetch(fileInfo.download_url);
                const blob = await fileResponse.blob();
                window.open(URL.createObjectURL(blob), '_blank');
              }
            }
          }
        })
        .catch((error: any) => {
          if ([401, 404, 500].includes(error?.request?.status)) {
            handleError(error, toastBottom);
          }
        });
    } else {
      toastBottom.current.show({ severity: 'error', summary: 'Error', detail: 'Please log in to access the release notes.', life: 3000 });
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    }
  };

  return (
    <div className="help-center-container">
      <Toast ref={toastBottom} position="bottom-right" />
      {apiLoading && (
        <div className="card">
          <SpinnerOverlay />
        </div>
      )}

      <div className="flex header">
        <div className="common-page-title">Help Center</div>
      </div>

      <div className="help-center-main-content">
        {/* <div className="faq-content"> */}
        <div className="flex" style={{ justifyContent: 'space-between', marginBottom: '23px' }}>
          <div className="faq-title">Top FAQs</div>
          <div>
            <div className="header-nav-item disabled">
              <form onSubmit={(e) => onSearchSubmit(e)} method="get" className="relative">
                <IconField iconPosition="left" className="header-search-input">
                  <InputIcon>
                    <img src={SearchIcon} alt="search" />
                  </InputIcon>
                  <InputText disabled value={searchText} placeholder="Search" style={{ width: '543px', paddingTop: '7px', paddingBottom: '7px', paddingRight: '30px', fontSize: '12px', borderRadius: '7px', border: '1px solid #5C5C5C' }} onChange={(e) => setSearchText(e.target.value)} />
                </IconField>
                {searchText && searchText !== '' && (
                  <img
                    src={CloseIcon}
                    alt="remove"
                    style={{ position: 'absolute', top: '11px', right: '10px', cursor: 'pointer' }}
                    onClick={() => setSearchText('')}
                  />
                )}
              </form>
            </div>
          </div>
        </div>
        <Accordion className="faq-accordion">
          {faqs.map((faq, index) => (
            <AccordionTab key={index} header={faq.question}>
              <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
            </AccordionTab>
          ))}
        </Accordion>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button disabled>View All</Button>
        </div>
        <div style={{ borderTop: '1px solid #A7ACC1', marginTop: '16px' }} />

        <div style={{ paddingTop: '24px' }}>
          <div className="faq-text" style={{ marginBottom: '24px', fontWeight: 600, fontSize: '20px' }}>
            User Manual Link:
            &nbsp;
            <span
              onClick={() => getUserManual()}
              style={{ color: '#365EED', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500 }}
            >
              BAYER-LMR - User Manual
            </span>

          </div>
          <div className="faq-text" style={{ borderTop: '1px solid #A7ACC1', marginBottom: '24px', fontWeight: 600, fontSize: '20px', paddingTop: '20px' }}>
            What&apos;s New?:
            &nbsp;
            <span
              onClick={() => getReleaseNotes()}
              style={{ color: '#365EED', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500 }}
            >
              eLMRoot Latest Release notes
            </span>
          </div>
          <div className="faq-text" style={{ borderTop: '1px solid #A7ACC1', marginBottom: '24px', fontWeight: 600, fontSize: '20px', paddingTop: '20px' }}>
            Facing issues ? Fill out this form:
            &nbsp;
            {/* <span style={{ color: '#365EED', textDecoration: 'underline' }}>Just Fill this form</span> */}
            <a
              href="https://forms.office.com/Pages/ResponsePage.aspx?id=e7Oy_KBda0abgwAUtnp8eGNg5whi1ZhAhOht4eRDhKdURjFZOUxJSFBHTkxNUTdKVFNEUVhCUlJTUy4u"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#365EED', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500 }}
            >
              Bayer Consumer Health&apos;s LMR Platform Feedback
            </a>

          </div>
          <div style={{ borderTop: '1px solid #A7ACC1', marginBottom: '16px' }} />

          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontWeight: 600, fontSize: '20px', marginBottom: '8px' }}>
              Bayer Contact:
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '25px' }}>
              <div className="faq-text">
                Alberto Bentolila :
                {' '}
                <a className="faq-text" href="mailto:alberto.bentolila@bayer.com" style={{ color: '#365EED', textDecoration: 'underline' }}>
                  alberto.bentolila@bayer.com
                </a>
              </div>

              <div className="faq-text">
                Jessica Labita :
                {' '}
                <a className="faq-text" href="mailto:jessica.labita@bayer.com" style={{ color: '#365EED', textDecoration: 'underline' }}>
                  jessica.labita@bayer.com
                </a>
              </div>

              <div className="faq-text">
                Jennifer Miller :
                {' '}
                <a className="faq-text" href="mailto:jennifer.miller1@bayer.com" style={{ color: '#365EED', textDecoration: 'underline' }}>
                  jennifer.miller1@bayer.com
                </a>
              </div>
            </div>
          </div>

          {/* <div style={{ borderTop: '1px solid #A7ACC1', marginBottom: '16px' }} />

          <div className="faq-text" style={{ marginTop: '12px', fontWeight: 600, fontSize: '20px' }}>
            Contact us :
            <a href="mailto: LMR.Support@cyrino.com" className="faq-text" style={{ marginLeft: '8px', color: '#365EED' }}>
              LMR.Support@cyrino.com
            </a>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
